import { useState, useMemo, memo, useCallback } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Skeleton,
  TablePagination,
  Toolbar,
  Select,
  FormControl,
  MenuItem as SelectMenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { useNavigate } from "react-router-dom";
import { formatDate } from "./formatDate";

// Memoized columns configuration
const COLUMNS = [
  { key: "name", label: "Plan Name", mobile: true },
  { key: "influencers", label: "Total Influencers", mobile: false },
  { key: "lists", label: "Total Lists", mobile: false },
  { key: "createdBy", label: "Created By", mobile: false },
  { key: "createdOn", label: "Created On", mobile: false },
];

// Memoized Plan Row Menu component
const PlanRowMenu = memo(({ plan, onArchiveToggle, onDelete, onDownload, isArchived, isLoading, isDownloading }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback((e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  }, []);

  const handleArchiveToggle = useCallback((e) => {
    handleClose(e);
    onArchiveToggle(plan);
  }, [handleClose, onArchiveToggle, plan]);

  const handleDelete = useCallback((e) => {
    handleClose(e);
    onDelete(plan);
  }, [handleClose, onDelete, plan]);

  const handleDownload = useCallback((e) => {
    handleClose(e);
    onDownload(plan);
  }, [handleClose, onDownload, plan]);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        disabled={isLoading}
        sx={{
          opacity: isLoading ? 0.5 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            boxShadow: 3,
            borderRadius: 2,
            minWidth: 150,
          }
        }}
      >
        <MenuItem onClick={handleDownload} disabled={isLoading || isDownloading}>
          <ListItemIcon>
            <FileDownloadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={isDownloading ? "Preparing file…" : "Download"} />
        </MenuItem>
        <MenuItem onClick={handleArchiveToggle} disabled={isLoading}>
          <ListItemIcon>
            <Inventory2OutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={isArchived ? "Unarchive" : "Archive"} />
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          disabled={isLoading}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
    </>
  );
});

PlanRowMenu.displayName = 'PlanRowMenu';

// Separate component for Plan Row to enable individual re-renders
const PlanRow = memo(({
  plan,
  isArchived,
  onArchiveToggle,
  onDelete,
  onDownload,
  downloadingPlanId,
  isLoading,
  isMobile,
  onClick
}) => {
  const handleRowClick = useCallback(() => {
    onClick(plan.id);
  }, [onClick, plan.id]);

  return (
    <TableRow
      hover
      onClick={handleRowClick}
      sx={{
        cursor: "pointer",
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          '&:last-child': {
            borderBottom: 'none',
          },
        }),
      }}
    >
      <TableCell
        sx={{
          borderColor: "grey.100",
          py: isMobile ? 2 : 1.5,
          ...(isMobile && {
            border: 'none',
            width: '100%',
          }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            variant="rounded"
            src={plan.brandLogo}
            sx={{
              width: isMobile ? 32 : 36,
              height: isMobile ? 32 : 36,
              bgcolor: "grey.100",
              flexShrink: 0,
            }}
          >
            {plan.brand?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontWeight: 700,
              lineHeight: 1.3,
              fontSize: isMobile ? '0.875rem' : '1rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {plan.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {plan.brand}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {!isMobile && (
        <>
          <TableCell sx={{ borderColor: "grey.100", py: 1.5 }}>
            <Typography fontWeight={500}>
              {plan.influencerCount ?? 0}
            </Typography>
          </TableCell>
          <TableCell sx={{ borderColor: "grey.100", py: 1.5 }}>
            <Typography fontWeight={500}>
              {plan.listCount ?? 0}
            </Typography>
          </TableCell>
          <TableCell sx={{ borderColor: "grey.100", py: 1.5 }}>
            {plan.createdBy}
          </TableCell>
          <TableCell sx={{ borderColor: "grey.100", py: 1.5 }}>
            {formatDate(plan.createdOn)}
          </TableCell>
        </>
      )}

      <TableCell
        align="right"
        sx={{
          borderColor: "grey.100",
          py: isMobile ? 1 : 1.5,
          ...(isMobile && {
            border: 'none',
            position: 'absolute',
            top: 8,
            right: 8,
            width: 'auto',
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <PlanRowMenu
          plan={plan}
          isArchived={isArchived}
          onArchiveToggle={onArchiveToggle}
          onDelete={onDelete}
          onDownload={onDownload}
          isLoading={isLoading}
          isDownloading={plan.id === downloadingPlanId}
        />
      </TableCell>

      {isMobile && (
        <TableCell
          sx={{
            border: 'none',
            pt: 0,
            pb: 2,
            width: '100%',
          }}
        >
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleAltOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {plan.influencerCount ?? 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ListAltOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {plan.listCount ?? 0}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 0.5,
          }}>
            <Typography variant="caption" color="text.secondary">
              By: {plan.createdBy}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(plan.createdOn)}
            </Typography>
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
});

PlanRow.displayName = 'PlanRow';

// Pagination Controls component
const PaginationControls = memo(({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  isMobile,
  rowsPerPageOptions = [5, 10, 25, 50],
  isLoading = false,
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const startIndex = page * rowsPerPage + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, count);

  if (isMobile) {
    return (
      <Toolbar sx={{
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'space-between',
        minHeight: { xs: 48, sm: 56 },
        px: { xs: 1, sm: 2 },
      }}>
        <FormControl size="small" sx={{ minWidth: 70 }} disabled={isLoading}>
          <Select
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            displayEmpty
            sx={{ fontSize: '0.75rem' }}
          >
            {rowsPerPageOptions.map((option) => (
              <SelectMenuItem key={option} value={option}>
                {option}
              </SelectMenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          {`${startIndex}-${endIndex} of ${count}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0 }}>
          <IconButton
            size="small"
            onClick={() => onPageChange(null, page - 1)}
            disabled={page === 0 || isLoading}
            sx={{ padding: 0.5 }}
          >
            <Typography>‹</Typography>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onPageChange(null, page + 1)}
            disabled={page >= totalPages - 1 || isLoading}
            sx={{ padding: 0.5 }}
          >
            <Typography>›</Typography>
          </IconButton>
        </Box>
      </Toolbar>
    );
  }

  return (
    <TablePagination
      rowsPerPageOptions={rowsPerPageOptions}
      component="div"
      count={count}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      labelRowsPerPage="Rows:"
      sx={{
        borderBottom: 'none',
        '& .MuiTablePagination-select': {
          fontSize: '0.875rem',
        },
        '& .MuiTablePagination-displayedRows': {
          fontSize: '0.875rem',
        },
      }}
    />
  );
});

PaginationControls.displayName = 'PaginationControls';

// Main PlansTable component
const PlansTable = memo(({
  plans = [],
  isArchived = false,
  onArchiveToggle,
  onDelete,
  onDownload,
  downloadingPlanId = null,
  isLoading = false,
  initialPage = 0,
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25, 50],
  onPageChange: externalPageChange,
  onRowsPerPageChange: externalRowsPerPageChange,
  totalCount,
  serverSidePagination = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Local pagination state
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Memoized display plans with client-side pagination
  const displayPlans = useMemo(() => {
    if (serverSidePagination) {
      return plans;
    }

    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return plans.slice(start, end);
  }, [plans, page, rowsPerPage, serverSidePagination]);

  // Calculate total count for pagination
  const totalCountValue = useMemo(() => {
    if (serverSidePagination) {
      return totalCount || plans.length;
    }
    return plans.length;
  }, [plans, serverSidePagination, totalCount]);

  // Memoized visible columns based on screen size
  const visibleColumns = useMemo(() => {
    if (isMobile) {
      return COLUMNS.filter(col => col.key === 'name' || col.key === 'createdOn');
    }
    if (isTablet) {
      return COLUMNS.filter(col => col.key !== 'createdBy');
    }
    return COLUMNS;
  }, [isMobile, isTablet]);

  // Handle row click
  const handleRowClick = useCallback((planId) => {
    navigate(`/plans-summary/${planId}`);
  }, [navigate]);

  // Handle page change
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
    if (externalPageChange) {
      externalPageChange(newPage);
    }
  }, [externalPageChange]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (externalRowsPerPageChange) {
      externalRowsPerPageChange(newRowsPerPage);
    }
  }, [externalRowsPerPageChange]);

  // Loading skeleton
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ p: 2 }}>
          {Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 2,
                py: 1.5,
                borderBottom: index < Math.min(rowsPerPage, 10) - 1 ? '1px solid' : 'none',
                borderColor: 'grey.100',
                alignItems: 'center',
              }}
            >
              <Skeleton variant="rounded" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
              {!isMobile && (
                <>
                  <Skeleton variant="text" width={80} height={24} />
                  <Skeleton variant="text" width={60} height={24} />
                  <Skeleton variant="text" width={60} height={24} />
                </>
              )}
              <Skeleton variant="text" width={isMobile ? 60 : 100} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          ))}
        </Box>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
          <Skeleton variant="rectangular" height={40} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ overflowX: 'auto', flex: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    border: 0,
                    py: 2,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    ...(col.key === 'name' && { minWidth: isMobile ? 200 : 250 }),
                    ...(isMobile && col.key !== 'name' && { minWidth: 120 }),
                    bgcolor: 'background.paper',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  border: 0,
                  py: 2,
                  width: isMobile ? 50 : 60,
                  minWidth: isMobile ? 50 : 60,
                  bgcolor: 'background.paper',
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayPlans.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length + 1}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                    No plans found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isArchived ? "No archived plans" : "Create your first plan to get started"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayPlans.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  isArchived={isArchived}
                  onArchiveToggle={onArchiveToggle}
                  onDelete={onDelete}
                  onDownload={onDownload}
                  downloadingPlanId={downloadingPlanId}
                  isLoading={isLoading}
                  isMobile={isMobile}
                  onClick={handleRowClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      {totalCountValue > 0 && (
        <Box sx={{
          borderTop: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'background.paper',
        }}>
          <PaginationControls
            count={totalCountValue}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            isMobile={isMobile}
            rowsPerPageOptions={rowsPerPageOptions}
            isLoading={isLoading}
          />
        </Box>
      )}
    </Box>
  );
});

PlansTable.displayName = 'PlansTable';

export default PlansTable;
